// components/promotions/PromotionTimeline.jsx
// Horizontal bar chart showing promotions across months with scrollable past/future.

import { useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Tooltip } from '@mui/material';
import StatusChip from '../common/StatusChip';
import { useFormatters } from '../../hooks/useFormatters';
import { useStoreToday } from '../../hooks/useStoreToday';
import { promotionEffectiveStatus } from '../../utils/promotionStatus';
import './Promotions.css';

const PAST_MONTHS = 12;
const FUTURE_MONTHS = 12;
const TOTAL_MONTHS = PAST_MONTHS + FUTURE_MONTHS;
const LABEL_PAD = 16; // right padding for label text

// Measure the widest promotion name using an offscreen canvas
function measureMaxLabelWidth(names, font = '12px "Segoe UI", sans-serif') {
  if (names.length === 0) return 80;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.font = font;
  let max = 0;
  for (const n of names) {
    const w = ctx.measureText(n).width;
    if (w > max) max = w;
  }
  return Math.ceil(max) + LABEL_PAD;
}

function PromotionTimeline({ promotions, onEdit }) {
  const { t } = useTranslation();
  const { formatDate, formatMonthYear } = useFormatters();
  const scrollRef = useRef(null);
  const todayRef = useRef(null);

  // The timeline is drawn around the STORE's current month, and its "today"
  // line sits on the store's day — the same anchor the status chips use. Every
  // date on this chart is a calendar date the merchant scheduled, so reading
  // the window off the viewer's clock (or UTC, as this did) slid the whole
  // chart by a day against the promotions drawn on it.
  const storeToday = useStoreToday();

  // Generate months: PAST_MONTHS back + current + FUTURE_MONTHS-1 forward.
  // Empty without an anchor — there is no honest window to draw, and the
  // component returns null below rather than centring on a guessed month.
  const months = useMemo(() => {
    if (!storeToday) return [];
    const result = [];
    const anchorYear = Number(storeToday.slice(0, 4));
    const anchorMonth = Number(storeToday.slice(5, 7)) - 1;
    for (let i = -PAST_MONTHS; i < FUTURE_MONTHS; i++) {
      const d = new Date(Date.UTC(anchorYear, anchorMonth + i, 1));
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
      result.push({
        key,
        label: formatMonthYear(key),
        year: d.getUTCFullYear(),
        month: d.getUTCMonth(),
        date: d,
      });
    }
    return result;
  }, [formatMonthYear, storeToday]);

  // Compute label column width from promotion names
  const labelWidth = useMemo(() => {
    const names = promotions.map(p => p.name || '');
    return Math.max(80, Math.min(300, measureMaxLabelWidth(names)));
  }, [promotions]);

  // Timeline bounds derived from months. Memoized so downstream useMemos
  // (bars, monthGridlines) don't invalidate every render — without this,
  // `new Date(...)` runs every render and the dep references are fresh.
  const { timelineStart, timelineEnd, totalDays } = useMemo(() => {
    if (months.length === 0) return { timelineStart: null, timelineEnd: null, totalDays: 0 };
    const start = months[0].date;
    const end = new Date(Date.UTC(
      months[months.length - 1].date.getUTCFullYear(),
      months[months.length - 1].date.getUTCMonth() + 1,
      0,
    ));
    return {
      timelineStart: start,
      timelineEnd: end,
      totalDays: (end - start) / (1000 * 60 * 60 * 24),
    };
  }, [months]);

  // Today marker position. The marker is the store's day placed on a UTC-built
  // axis, which is consistent because both are calendar days: the axis months
  // are Date.UTC midnights and so is this.
  const todayPct = useMemo(() => {
    if (!storeToday || !totalDays) return null;
    const todayUTC = new Date(`${storeToday}T00:00:00Z`);
    const days = (todayUTC - timelineStart) / (1000 * 60 * 60 * 24);
    return Math.max(0, Math.min(100, (days / totalDays) * 100));
  }, [storeToday, timelineStart, totalDays]);

  // Calculate bar positions for each promotion. The status shown on a bar is
  // the shared rule (utils/promotionStatus.js).
  const bars = useMemo(() => {
    if (!timelineStart) return [];
    return promotions.map(promo => {
      const start = new Date(promo.start_date + 'T00:00:00Z');
      const end = new Date(promo.end_date + 'T00:00:00Z');
      const status = promotionEffectiveStatus(promo, storeToday);

      // Clamp to timeline range
      const clampedStart = Math.max(start, timelineStart);
      const clampedEnd = Math.min(end, timelineEnd);

      const leftDays = (clampedStart - timelineStart) / (1000 * 60 * 60 * 24);
      const widthDays = (clampedEnd - clampedStart) / (1000 * 60 * 60 * 24) + 1;

      const leftPct = Math.max(0, (leftDays / totalDays) * 100);
      const widthPct = Math.max(1, (widthDays / totalDays) * 100);

      // Pre/post adjustment bars
      let preLeftPct = null;
      let preWidthPct = null;
      if (promo.pre_adjustment_pct) {
        const preStart = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() - 1, 1));
        const preEnd = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 0));
        const cPreStart = Math.max(preStart, timelineStart);
        const cPreEnd = Math.min(preEnd, timelineEnd);
        const pLeft = (cPreStart - timelineStart) / (1000 * 60 * 60 * 24);
        const pWidth = (cPreEnd - cPreStart) / (1000 * 60 * 60 * 24) + 1;
        preLeftPct = Math.max(0, (pLeft / totalDays) * 100);
        preWidthPct = Math.max(0.5, (pWidth / totalDays) * 100);
      }

      let postLeftPct = null;
      let postWidthPct = null;
      if (promo.post_adjustment_pct) {
        const postStart = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() + 1, 1));
        const postEnd = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() + 2, 0));
        const cPostStart = Math.max(postStart, timelineStart);
        const cPostEnd = Math.min(postEnd, timelineEnd);
        const pLeft = (cPostStart - timelineStart) / (1000 * 60 * 60 * 24);
        const pWidth = (cPostEnd - cPostStart) / (1000 * 60 * 60 * 24) + 1;
        postLeftPct = Math.max(0, (pLeft / totalDays) * 100);
        postWidthPct = Math.max(0.5, (pWidth / totalDays) * 100);
      }

      return {
        ...promo,
        effectiveStatus: status,
        leftPct,
        widthPct,
        preLeftPct,
        preWidthPct,
        postLeftPct,
        postWidthPct,
        visible: clampedEnd >= timelineStart && clampedStart <= timelineEnd,
      };
    }).filter(b => b.visible);
  }, [promotions, storeToday, timelineStart, timelineEnd, totalDays]);

  // Month boundary positions (percentage) for gridlines on tracks
  const monthGridlines = useMemo(() => {
    if (!timelineStart) return [];
    return months.map(m => {
      const days = (m.date - timelineStart) / (1000 * 60 * 60 * 24);
      return (days / totalDays) * 100;
    }).filter(pct => pct > 0 && pct < 100);
  }, [months, timelineStart, totalDays]);

  const statusStyle = (status) => {
    switch (status) {
      case 'active':    return { backgroundColor: 'var(--promo-active)', opacity: 1 };
      case 'scheduled': return { backgroundColor: 'var(--promo-active)', opacity: 0.5 };
      case 'completed': return { backgroundColor: 'var(--promo-draft)',  opacity: 0.5 };
      case 'draft':
      default:          return { backgroundColor: 'var(--promo-draft)',  opacity: 1 };
    }
  };

  // Auto-scroll so "today" marker is visible (roughly centered)
  useEffect(() => {
    if (todayRef.current && scrollRef.current) {
      const container = scrollRef.current;
      const marker = todayRef.current;
      const scrollTarget = marker.offsetLeft - container.clientWidth / 3;
      container.scrollLeft = Math.max(0, scrollTarget);
    }
  }, []);

  // No store zone yet — the axis, the today line and the bars all hang off
  // the store's calendar, so there is nothing honest to draw until it lands.
  if (!storeToday) return null;

  if (promotions.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('promotions.noPromotions')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, p: 2 }}>
      <Box ref={scrollRef} className="promo-timeline" sx={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
        {/* Scrollable inner — fixed min-width forces horizontal scroll */}
        <Box sx={{ minWidth: `${months.length * 80 + labelWidth}px` }}>
          {/* Month headers — sticky top */}
          <Box className="promo-timeline-header" sx={{ position: 'sticky', top: 0, zIndex: 3, backgroundColor: 'var(--color-fill)', paddingLeft: `${labelWidth}px` }}>
            {months.map((m, i) => (
              <Box
                key={m.key}
                className={`promo-timeline-month${i === PAST_MONTHS ? ' promo-timeline-month-current' : ''}`}
              >
                <Typography variant="caption" color="text.secondary">{m.label}</Typography>
              </Box>
            ))}
          </Box>

          {/* Promotion bars */}
          <Box className="promo-timeline-body">
            {bars.map(bar => (
              <Box key={bar.PromotionID} className="promo-timeline-row">
                <Tooltip title={bar.name} describeChild>
                  <span className="promo-timeline-label" style={{ '--promo-timeline-label-width': labelWidth }}>
                    {bar.name}
                  </span>
                </Tooltip>
                <Box className="promo-timeline-track">
                  {/* Month gridlines */}
                  {monthGridlines.map((pct, i) => (
                    <Box key={i} sx={{ position: 'absolute', left: `${pct}%`, top: 0, bottom: 0, width: '1px', backgroundColor: 'var(--color-border)', opacity: 0.4, pointerEvents: 'none' }} />
                  ))}
                  {/* Today marker */}
                  <Box
                    ref={todayRef}
                    className="promo-timeline-today"
                    sx={{ left: `${todayPct}%` }}
                  />
                  {/* Pre-adjustment */}
                  {bar.preLeftPct != null && (
                    <Tooltip title={`${t('promotions.preAdjustment')}: ${bar.pre_adjustment_pct}%`}>
                      <Box
                        className="promo-timeline-bar promo-timeline-bar-pre"
                        sx={{ left: `${bar.preLeftPct}%`, width: `${bar.preWidthPct}%`, ...statusStyle(bar.effectiveStatus) }}
                      />
                    </Tooltip>
                  )}
                  {/* Main bar */}
                  <Tooltip title={`${bar.name} (${formatDate(bar.start_date)} — ${formatDate(bar.end_date)})`}>
                    <Box
                      className="promo-timeline-bar"
                      sx={{ left: `${bar.leftPct}%`, width: `${bar.widthPct}%`, ...statusStyle(bar.effectiveStatus), cursor: 'pointer' }}
                      onClick={() => onEdit?.(bar)}
                    />
                  </Tooltip>
                  {/* Post-adjustment */}
                  {bar.postLeftPct != null && (
                    <Tooltip title={`${t('promotions.postAdjustment')}: ${bar.post_adjustment_pct}%`}>
                      <Box
                        className="promo-timeline-bar promo-timeline-bar-pre"
                        sx={{ left: `${bar.postLeftPct}%`, width: `${bar.postWidthPct}%`, ...statusStyle(bar.effectiveStatus) }}
                      />
                    </Tooltip>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mt: 1.5, justifyContent: 'flex-end', flexShrink: 0 }}>
        <StatusChip label={t('promotions.status_active')} color="success" />
        <StatusChip label={t('promotions.status_scheduled')} color="info" />
        <StatusChip label={t('promotions.status_draft')} color="warning" />
        <StatusChip label={t('promotions.status_completed')} color="neutral" />
      </Box>
    </Box>
  );
}

export default PromotionTimeline;
